import { useState, useEffect, useCallback } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { CampaignTable, Campaign } from "../components/email-campaigns/CampaignTable";
import { Modal } from "../components/ui/Modal";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/ToastContainer";
import { campaignService } from "../services/campaignService";
import { parseApiError } from "../utils/errorHandler";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Send, Calendar, Loader2 } from "lucide-react";

export default function EmailCampaignsPage() {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCampaigns, setTotalCampaigns] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isSendOpen, setIsSendOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);

  const fetchCampaigns = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: pageSize,
        sortBy: "createdAt",
        order: "desc",
      };

      if (statusFilter !== "all") {
        params.status = statusFilter;
      }

      if (typeFilter !== "all") {
        params.type = typeFilter;
      }

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await campaignService.getAllCampaigns(params);
      setCampaigns((response.data || []) as unknown as Campaign[]);
      setTotalCampaigns(response.total || 0);
    } catch (error) {
      console.error("Failed to fetch campaigns:", error);
      const apiError = parseApiError(error);
      showError(apiError.message, "Failed to Load Campaigns");
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, statusFilter, typeFilter, searchTerm, showError]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleEditClick = (campaign: Campaign) => {
    navigate(`/email-campaigns/edit/${campaign._id}`);
  };

  const handleViewClick = (campaign: Campaign) => {
    navigate(`/email-campaigns/${campaign._id}`);
  };

  const handleDeleteClick = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setIsDeleteOpen(true);
  };

  const handleSendClick = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setIsSendOpen(true);
  };

  const handleCancelClick = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setIsCancelOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedCampaign) return;

    try {
      await campaignService.deleteCampaign(selectedCampaign._id);
      showSuccess("Campaign deleted successfully", "Success");
      setIsDeleteOpen(false);
      setSelectedCampaign(null);
      fetchCampaigns();
    } catch (error) {
      console.error("Failed to delete campaign:", error);
      const apiError = parseApiError(error);
      showError(apiError.message, "Delete Failed");
    }
  };

  const handleSend = async () => {
    if (!selectedCampaign) return;

    try {
      await campaignService.sendCampaign(selectedCampaign._id);
      showSuccess("Campaign is being sent", "Success");
      setIsSendOpen(false);
      setSelectedCampaign(null);
      fetchCampaigns();
    } catch (error) {
      console.error("Failed to send campaign:", error);
      const apiError = parseApiError(error);
      showError(apiError.message, "Send Failed");
    }
  };

  const handleCancel = async () => {
    if (!selectedCampaign) return;

    try {
      await campaignService.cancelCampaign(selectedCampaign._id);
      showSuccess("Campaign cancelled successfully", "Success");
      setIsCancelOpen(false);
      setSelectedCampaign(null);
      fetchCampaigns();
    } catch (error) {
      console.error("Failed to cancel campaign:", error);
      const apiError = parseApiError(error);
      showError(apiError.message, "Cancel Failed");
    }
  };

  const handleCreateClick = () => {
    navigate("/email-campaigns/create");
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleTypeFilterChange = (type: string) => {
    setTypeFilter(type);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#102040]">
              Email Campaigns
            </h1>
            <p className="text-sm text-[#989797] mt-1">
              Create and manage marketing email campaigns for your students
            </p>
          </div>
        </div>

        <CampaignTable
          campaigns={campaigns}
          description="Create and manage email campaigns for marketing and communication."
          isLoading={isLoading}
          totalCampaigns={totalCampaigns}
          currentPage={currentPage}
          pageSize={pageSize}
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          typeFilter={typeFilter}
          onSearchChange={handleSearchChange}
          onStatusFilterChange={handleStatusFilterChange}
          onTypeFilterChange={handleTypeFilterChange}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onAddClick={handleCreateClick}
          onEditClick={handleEditClick}
          onDeleteClick={handleDeleteClick}
          onViewClick={handleViewClick}
          onSendClick={handleSendClick}
          onCancelClick={handleCancelClick}
        />

        <Modal
          isOpen={isDeleteOpen}
          onClose={() => {
            setIsDeleteOpen(false);
            setSelectedCampaign(null);
          }}
          title="Delete Campaign"
        >
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg border border-red-100">
              <AlertTriangle size={24} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-[#102040]">
                  Are you sure you want to delete "{selectedCampaign?.title}"?
                </p>
                <p className="text-xs text-[#989797] mt-1">
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteOpen(false);
                  setSelectedCampaign(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete Campaign
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={isSendOpen}
          onClose={() => {
            setIsSendOpen(false);
            setSelectedCampaign(null);
          }}
          title="Send Campaign"
        >
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <Send size={24} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-[#102040]">
                  Are you sure you want to send "{selectedCampaign?.title}"?
                </p>
                <p className="text-xs text-[#989797] mt-1">
                  This will send the email immediately to{" "}
                  {selectedCampaign?.targeting.recipientsCount || 0} recipients.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setIsSendOpen(false);
                  setSelectedCampaign(null);
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSend}>
                Send Now
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={isCancelOpen}
          onClose={() => {
            setIsCancelOpen(false);
            setSelectedCampaign(null);
          }}
          title="Cancel Scheduled Campaign"
        >
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-100">
              <Calendar size={24} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-[#102040]">
                  Are you sure you want to cancel "{selectedCampaign?.title}"?
                </p>
                <p className="text-xs text-[#989797] mt-1">
                  The campaign will not be sent as scheduled.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setIsCancelOpen(false);
                  setSelectedCampaign(null);
                }}
              >
                Keep Scheduled
              </Button>
              <Button
                onClick={handleCancel}
                className="bg-amber-600 hover:bg-amber-700"
              >
                Cancel Campaign
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </DashboardLayout>
  );
}
